import { apiClient } from './apiClient';

export interface CreateGeneratedReportDto {
  siteIds: string[];  // Changed from siteId to support multiple sites
  fromDate: string;
  toDate: string;
  reportIds: string[];
  guardIds?: string[];  // Optional: Filter by specific guards
}

export interface GuardInfoDto {
  id: string;
  account: string;
  firstName: string;
  lastName: string;
  fullName: string;
  reportCount: number;
}

export interface CreateGeneratedReportFromSingleReportDto {
  reportId: string;
  title?: string;
}

export interface GeneratedReportImageDto {
  id: string;
  imageUrl: string;
  caption?: string;
  order: number;
}

export interface GeneratedReportItemDto {
  id: string;
  title: string;
  details: string;
  status: string;
  location?: string;
  guardName?: string;
  reportedAt: string;
  order: number;
  isCheckInOut?: boolean;
  images: GeneratedReportImageDto[];
}

export interface GeneratedReportDto {
  id: string;
  shareableToken: string;
  title: string;
  siteId?: string;
  siteName: string;
  createdByUserName: string;
  createdAt: string;
  fromDate: string;
  toDate: string;
  shareableUrl: string;
  overviewSummary?: string;
  lastUpdated?: string;
  items: GeneratedReportItemDto[];
}

/** Lightweight DTO for list views — no items or images. */
export interface GeneratedReportSummaryDto {
  id: string;
  shareableToken: string;
  title: string;
  siteId?: string;
  siteName: string;
  createdByUserName: string;
  createdAt: string;
  fromDate: string;
  toDate: string;
  shareableUrl: string;
  itemCount: number;
}

export interface UpdateGeneratedReportDto {
  title?: string;
  overviewSummary?: string;
}

export interface SendReportDto {
  method: 'sms' | 'email';
  recipient: string;
}

export interface CreateReportItemDto {
  title: string;
  details: string;
  status: string;
  location?: string;
  guardName?: string;
  reportedAt: string;
}

export interface UpdateReportItemDto {
  title?: string;
  details?: string;
  status?: string;
  location?: string;
  guardName?: string;
  reportedAt?: string;
}

export interface PublicReportItemDto {
  id: string;
  smartRevision: string;
  aiTitle: string;
  reportedAt: string;
  incidentStatus: string;
  images: string[];
  latitude?: number;
  longitude?: number;
  guardName: string;
  location: string;
  siteName: string;
  isCheckInOut?: boolean;
}

export interface PublicReportDto {
  title: string;
  overviewSummary: string;
  companyName: string;
  companyLogo: string;
  siteName: string;
  clientName: string;
  fromDate: string;
  toDate: string;
  reports: PublicReportItemDto[];
  siteLatitude?: number;
  siteLongitude?: number;
  siteAddress: string;
}

export const generatedReportsService = {
  // Admin endpoints (authenticated)
  async createGeneratedReport(data: CreateGeneratedReportDto): Promise<GeneratedReportDto> {
    const response = await apiClient.post('/generatedreports', data);
    return response.data as GeneratedReportDto;
  },

  async getGeneratedReports(): Promise<GeneratedReportSummaryDto[]> {
    const response = await apiClient.get('/generatedreports');
    return response.data as GeneratedReportSummaryDto[];
  },

  async getGeneratedReport(id: string): Promise<GeneratedReportDto> {
    const response = await apiClient.get(`/generatedreports/${id}`);
    return response.data as GeneratedReportDto;
  },

  async updateGeneratedReport(id: string, updates: UpdateGeneratedReportDto): Promise<void> {
    const response = await apiClient.put(`/generatedreports/${id}`, updates);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update report');
    }
  },

  async updateReportOverview(id: string, summary: string): Promise<void> {
    const response = await apiClient.put(`/generatedreports/${id}/overview`, { overviewSummary: summary });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update overview');
    }
  },

  async createReportItem(reportId: string, item: CreateReportItemDto): Promise<GeneratedReportItemDto> {
    const response = await apiClient.post(`/generatedreports/${reportId}/items`, item);
    return response.data as GeneratedReportItemDto;
  },

  async updateReportItem(reportId: string, itemId: string, updates: UpdateReportItemDto): Promise<void> {
    const response = await apiClient.put(`/generatedreports/${reportId}/items/${itemId}`, updates);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update report item');
    }
  },

  async deleteReportItem(reportId: string, itemId: string): Promise<void> {
    await apiClient.delete(`/generatedreports/${reportId}/items/${itemId}`);
  },

  async reorderReportItems(reportId: string, itemIds: string[]): Promise<void> {
    const response = await apiClient.put(`/generatedreports/${reportId}/items/reorder`, { itemIds });
    if (!response.success) {
      throw new Error(response.message || 'Failed to reorder items');
    }
  },

  async createGeneratedReportFromSingleReport(data: CreateGeneratedReportFromSingleReportDto): Promise<GeneratedReportDto> {
    const response = await apiClient.post('/generatedreports/from-single-report', data);
    return response.data as GeneratedReportDto;
  },

  async deleteGeneratedReport(id: string): Promise<void> {
    await apiClient.delete(`/generatedreports/${id}`);
  },

  async sendReport(reportId: string, data: SendReportDto): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`/generatedreports/${reportId}/send`, data);
    if (response.success && response.data) {
      return response.data as { success: boolean; message: string };
    }
    throw new Error(response.message || 'Failed to send report');
  },

  // Public endpoint (no authentication)
  async getPublicReport(token: string): Promise<PublicReportDto> {
    const response = await apiClient.get(`/publicreports/${token}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to load report');
    }
    return response.data as PublicReportDto;
  },

  // Public report editing endpoints (authenticated)
  async updatePublicReportOverview(token: string, summary: string): Promise<void> {
    const response = await apiClient.put(`/publicreports/${token}/overview`, { overviewSummary: summary });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update overview');
    }
  },

  async updatePublicReportItemTitle(token: string, itemId: string, title: string): Promise<void> {
    const response = await apiClient.put(`/publicreports/${token}/items/${itemId}/title`, { title });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update item title');
    }
  },

  async updatePublicReportTitle(token: string, title: string): Promise<void> {
    const response = await apiClient.put(`/publicreports/${token}/title`, { title });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update title');
    }
  },

  async updatePublicReportItemDetails(token: string, itemId: string, details: string): Promise<void> {
    const response = await apiClient.put(`/publicreports/${token}/items/${itemId}/details`, { details });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update item details');
    }
  },

  async updatePublicReportItemImages(token: string, itemId: string, imageUrls: string[]): Promise<void> {
    const response = await apiClient.put(`/publicreports/${token}/items/${itemId}/images`, { imageUrls });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update item images');
    }
  },

  // Image upload for generated reports (admin context)
  async uploadGeneratedReportImage(reportId: string, imageBase64: string, description?: string, order?: number): Promise<GeneratedReportImageDto> {
    const response = await apiClient.post(`/generatedreports/${reportId}/images`, {
      imageBase64,
      description,
      order
    });
    return response.data as GeneratedReportImageDto;
  },

  // Image upload for public reports (token-based)
  async uploadPublicReportImage(token: string, reportItemId: string, imageBase64: string, description?: string, order?: number): Promise<GeneratedReportImageDto> {
    const response = await apiClient.post(`/publicreports/${token}/items/${reportItemId}/images`, {
      imageBase64,
      description,
      order
    });
    return response.data as GeneratedReportImageDto;
  },

  // New method for multi-step wizard
  async getAvailableGuards(siteIds: string[], fromDate: Date, toDate: Date): Promise<GuardInfoDto[]> {
    const params = new URLSearchParams();
    siteIds.forEach(id => params.append('siteIds', id));
    params.append('fromDate', fromDate.toISOString());
    params.append('toDate', toDate.toISOString());
    
    const response = await apiClient.get(`/generatedreports/available-guards?${params.toString()}`);
    return response.data as GuardInfoDto[];
  }
};
